/**
 * The server's refusals, in the user's own words.
 *
 * The API answers a refusal with an English sentence that says exactly what is
 * wrong ("This overlaps another session for the same worker.") — often under a
 * code as broad as CONFLICT or NOT_FOUND, which on its own explains nothing. So
 * the UI recognises the sentence itself: each rule below names the i18n key
 * (`errors.server.<key>`) that explains it, and a pattern's named groups fill
 * that text's placeholders.
 *
 * A sentence the catalog does not know is still shown — quoted, under the kind
 * of refusal it was (see `explainError`) — so a new server message degrades to
 * English, never to "something went wrong". When the API gains a refusal, add
 * its rule here and its text to both locales.
 */
export interface ServerMessageRule {
  /** Under `errors.server.`. Several sentences may share one explanation. */
  key: string;
  /** The exact sentence, or a pattern whose named groups become the placeholders. */
  match: string | RegExp;
  /** Groups holding an operation code, shown by the operation's name. */
  stages?: readonly string[];
}

export const SERVER_MESSAGE_RULES: readonly ServerMessageRule[] = [
  { key: 'auth.originNotAllowed', match: 'Request origin is not allowed.' },
  { key: 'auth.missingRefresh', match: 'Missing refresh cookie.' },
  { key: 'auth.accountInactive', match: 'Account is inactive.' },
  { key: 'auth.accountInactive', match: 'User is inactive.' },
  { key: 'auth.accountLocked', match: 'Account is temporarily locked. Try again later.' },
  { key: 'auth.refreshInvalid', match: 'Invalid or expired refresh token.' },
  { key: 'auth.refreshInvalid', match: 'Refresh token not found or expired.' },
  { key: 'auth.refreshReuse', match: 'Refresh token reuse detected. Please log in again.' },
  { key: 'auth.passwordCommon', match: 'Password is too common. Choose a more unique one.' },
  { key: 'auth.currentPasswordWrong', match: 'Current password is incorrect.' },
  { key: 'auth.invalidCredentials', match: 'Invalid email or password.' },
  { key: 'auth.missingToken', match: 'Missing Bearer token.' },
  { key: 'auth.userGone', match: 'User no longer exists or is inactive.' },
  { key: 'auth.notAuthenticated', match: 'Not authenticated.' },
  { key: 'auth.missingPermission', match: /^Missing permission: (?<permission>.+)$/ },
  { key: 'auth.forbidden', match: 'Forbidden resource' },
  { key: 'auth.throttled', match: 'ThrottlerException: Too Many Requests' },
  {
    key: 'catalog.adminDeletedOnly',
    match: 'Only administrators can include deleted catalog items.',
  },
  { key: 'catalog.adminDeletedOnly', match: 'Only administrators can read deleted catalog items.' },
  {
    key: 'catalog.adminDeletedOnly',
    match: 'Only administrators can include deleted hardware items.',
  },
  {
    key: 'catalog.adminDeletedOnly',
    match: 'Only administrators can include deleted lighting items.',
  },
  {
    key: 'catalog.adminDeletedOnly',
    match: 'Only administrators can include deleted drawer systems.',
  },
  {
    key: 'catalog.adminDeletedOnly',
    match: 'Only administrators can include deleted drawer system variants.',
  },
  { key: 'catalog.adminDeletedOnly', match: 'Only administrators can include deleted edges.' },
  { key: 'catalog.adminDeletedOnly', match: 'Only administrators can include deleted materials.' },
  {
    key: 'catalog.nameExists',
    match: /^A catalog item of type "(?<type>.+)" with name "(?<name>.+)" already exists\.$/,
  },
  { key: 'catalog.libraryPermission', match: 'Missing permission to add items to your library.' },
  { key: 'catalog.itemNotFound', match: 'Catalog item not found.' },
  { key: 'catalog.lineItemNotFound', match: 'Project line item not found.' },
  { key: 'catalog.alreadyLinked', match: 'This line item is already linked to a catalog entry.' },
  {
    key: 'catalog.promoteTerminal',
    match: 'Cannot promote line items: project is in a terminal status.',
  },
  { key: 'catalog.skuExists', match: 'A catalog item with this SKU already exists.' },
  { key: 'catalog.skuExists', match: 'An edge with this SKU already exists.' },
  { key: 'catalog.hardwareNotFound', match: 'Hardware item not found.' },
  {
    key: 'catalog.internalCodeExists',
    match: 'A hardware item with this internal code already exists.',
  },
  {
    key: 'catalog.internalCodeExists',
    match: 'A lighting item with this internal code already exists.',
  },
  {
    key: 'catalog.internalCodeExists',
    match: 'A material with this internal code already exists.',
  },
  {
    key: 'catalog.internalCodeExists',
    match: 'A drawer system variant with this internal code already exists.',
  },
  { key: 'catalog.lightingNotFound', match: 'Lighting item not found.' },
  { key: 'catalog.drawerSystemNotFound', match: 'Drawer system not found.' },
  { key: 'catalog.drawerVariantNotFound', match: 'Drawer system variant not found.' },
  { key: 'catalog.drawerVariantNotFound', match: 'Drawer system variant not found' },
  {
    key: 'catalog.drawerSystemExists',
    match: 'A drawer system or variant with these details already exists.',
  },
  { key: 'catalog.edgeNotFound', match: 'Edge not found.' },
  { key: 'catalog.edgeNotFound', match: 'Edge not found' },
  { key: 'catalog.materialNotFound', match: 'Material not found.' },
  { key: 'catalog.materialNotFound', match: 'Material not found' },
  { key: 'import.fileRequired', match: 'File is required.' },
  { key: 'import.fileTooLarge', match: /^File too large \(max (?<bytes>\d+) bytes\)\.$/ },
  { key: 'import.unsupportedType', match: /^Unsupported mime type: (?<mime>.*)$/ },
  { key: 'import.rowVanished', match: 'Row vanished during apply.' },
  { key: 'import.rowFailed', match: 'Unknown error' },
  {
    key: 'import.drawerSystemMissing',
    match: /^Parent drawer system "(?<system>.+)" not found\.$/,
  },
  {
    key: 'import.variantBeforeSystem',
    match: /^Variant "(?<name>.+)" appears before any system header\.$/,
  },
  { key: 'import.sheetMissing', match: /^Sheet "(?<sheet>.+)" not found in workbook\.$/ },
  { key: 'import.edgeNameMissing', match: 'Missing edge name.' },
  { key: 'import.edgePriceInvalid', match: 'Missing or invalid edge price.' },
  { key: 'import.materialNameMissing', match: 'Missing material name.' },
  { key: 'import.priceInvalid', match: /^Missing or invalid price \(got "(?<value>.*)"\)\.$/ },
  { key: 'library.alreadyIn', match: 'Catalog item already in library.' },
  { key: 'library.notFound', match: 'Library item not found.' },
  { key: 'clients.adminDeletedOnly', match: 'Only administrators can include deleted clients.' },
  { key: 'clients.adminDeletedOnly', match: 'Only administrators can read deleted clients.' },
  { key: 'clients.hasProjects', match: 'Cannot delete a client that still has active projects.' },
  { key: 'clients.notFound', match: 'Client not found.' },
  { key: 'clients.notFound', match: 'Client not found or has been deleted.' },
  { key: 'clients.addressNotFound', match: 'Delivery address not found.' },
  { key: 'projects.notFound', match: 'Project not found.' },
  {
    key: 'projects.archived',
    match: 'Archived projects are read-only. Move it back to warranty to edit it.',
  },
  { key: 'projects.codeClash', match: 'Could not generate a unique project code; please retry.' },
  {
    key: 'projects.addressMismatch',
    match: 'Delivery address does not belong to the chosen client.',
  },
  { key: 'projects.agentMissing', match: 'Agent user not found or inactive.' },
  { key: 'projects.adminDeletedOnly', match: 'Only administrators can include deleted projects.' },
  { key: 'projects.adminDeletedOnly', match: 'Only administrators can read deleted projects.' },
  { key: 'projects.adminDeletedOnly', match: 'Only administrators can manage deleted projects.' },
  { key: 'projects.roleCannotEdit', match: 'Your role cannot edit project fields.' },
  { key: 'projects.roleCannotTransition', match: 'Your role cannot change project status.' },
  { key: 'projects.rejectionDetails', match: 'Rejection details are required (min 5 chars).' },
  { key: 'projects.rejectionDetails', match: 'Rejection requires details (min 5 chars).' },
  {
    key: 'projects.archiveEndpoint',
    match: 'Use POST /projects/:id/archive to archive a project.',
  },
  { key: 'projects.statusRace', match: 'Project status changed concurrently; please retry.' },
  {
    key: 'projects.mergeInScheduler',
    match: /^Project "(?<project>.+)" is in the scheduler and cannot be merged\.$/,
  },
  { key: 'projects.mergeCurrency', match: 'Projects in different currencies cannot be merged.' },
  { key: 'projects.notDeleted', match: 'Project is not deleted.' },
  { key: 'projects.purgeNotDeleted', match: 'Only a deleted project can be permanently removed.' },
  { key: 'projects.alreadyAssigned', match: 'Worker is already assigned to this project.' },
  { key: 'projects.assignmentNotFound', match: 'Assignment not found for this project.' },
  { key: 'projects.terminal', match: 'Project is in a terminal status.' },
  { key: 'projects.archiveOnlyWarranty', match: 'Only a project under warranty can be archived.' },
  {
    key: 'projects.unarchiveOnlyArchived',
    match: 'Only an archived project can be moved back to warranty.',
  },
  { key: 'users.notFound', match: 'User not found.' },
  { key: 'users.adminDeletedOnly', match: 'Only administrators can include deleted users.' },
  { key: 'users.adminDeletedOnly', match: 'Only administrators can read deleted users.' },
  { key: 'users.emailTaken', match: 'A user with this email already exists.' },
  { key: 'users.lastAdmin', match: 'Cannot demote or deactivate the last active administrator.' },
  { key: 'users.ownPassword', match: 'Use change-password to reset your own password.' },
  { key: 'users.deleteSelf', match: 'You cannot delete your own account.' },
  { key: 'users.deleteLastAdmin', match: 'Cannot delete the last active administrator.' },
  { key: 'users.ownRole', match: 'You cannot change your own role.' },
  { key: 'users.deactivateSelf', match: 'You cannot deactivate your own account.' },
  { key: 'items.terminal', match: 'Cannot modify line items: project is in a terminal status.' },
  {
    key: 'items.currencyMismatch',
    match:
      /^Line item currency \((?<item>[A-Z]+)\) does not match project currency \((?<project>[A-Z]+)\)\.(?: All line items in a project must share the same currency\.)?$/,
  },
  {
    key: 'items.pieceRefMissing',
    match: 'A material or edge referenced by a cabinet piece is missing or deleted.',
  },
  {
    key: 'items.hardwareGoneCabinet',
    match:
      'Hardware catalog item is missing, inactive, or deleted. Cannot attach it to the cabinet.',
  },
  {
    key: 'items.hardwareGoneProject',
    match:
      'Hardware catalog item is missing, inactive, or deleted. Cannot attach it to the project.',
  },
  {
    key: 'items.lightingGoneProject',
    match:
      'Lighting catalog item is missing, inactive, or deleted. Cannot attach it to the project.',
  },
  {
    key: 'items.catalogNotCabinet',
    match:
      'Catalog item is missing, deleted, or not of type CABINET. Cannot create a cabinet from it.',
  },
  {
    key: 'items.catalogNotCountertop',
    match:
      'Catalog item is missing, deleted, or not of type COUNTERTOP. Cannot create a countertop from it.',
  },
  {
    key: 'items.catalogNotDrawer',
    match: 'Catalog item is missing, deleted, or not of type DRAWER. Cannot create drawer from it.',
  },
  {
    key: 'items.catalogNotElement',
    match:
      'Catalog item is missing, deleted, or not of type ELEMENT. Cannot create a element from it.',
  },
  {
    key: 'items.catalogNotHardware',
    match:
      'Catalog item is missing, deleted, or not of type HARDWARE. Cannot create hardware from it.',
  },
  {
    key: 'items.catalogNotLighting',
    match:
      'Catalog item is missing, deleted, or not of type LIGHTING. Cannot create a lighting from it.',
  },
  {
    key: 'items.catalogNotPanel',
    match: 'Catalog item is missing, deleted, or not of type PANEL. Cannot create a panel from it.',
  },
  {
    key: 'items.dimensionsMissing',
    match: /^Catalog item lacks default dimensions and no overrides supplied: (?<fields>.+)\.$/,
  },
  { key: 'items.cabinetNotFound', match: 'Cabinet not found.' },
  { key: 'items.panelNotFound', match: 'Panel not found.' },
  { key: 'items.countertopNotFound', match: 'Countertop not found.' },
  { key: 'items.elementNotFound', match: 'Element not found.' },
  { key: 'items.lightingNotFound', match: 'Lighting not found.' },
  { key: 'items.drawerNotFound', match: 'Drawer not found.' },
  { key: 'items.hardwareNotFound', match: 'Hardware not found.' },
  { key: 'items.roomNotFound', match: 'Room not found.' },
  { key: 'items.areaNotFound', match: 'Area not found.' },
  { key: 'pricing.panelNeedsMaterial', match: 'Panel AUTO pricing requires materialId.' },
  { key: 'pricing.panelMaterialMissing', match: 'Panel material not found.' },
  { key: 'pricing.panelEdgeMissing', match: 'Panel edge not found.' },
  { key: 'pricing.countertopNeedsMaterial', match: 'Countertop AUTO pricing requires materialId.' },
  { key: 'pricing.countertopMaterialMissing', match: 'Countertop material not found.' },
  { key: 'pricing.countertopEdgeMissing', match: 'Countertop edge not found.' },
  {
    key: 'pricing.elementNeedsHardware',
    match: 'Element AUTO pricing requires at least one hardware item.',
  },
  {
    key: 'pricing.lightingNeedsCatalog',
    match: 'Lighting AUTO pricing requires lightingCatalogId.',
  },
  { key: 'pricing.lightingCatalogMissing', match: 'Lighting catalog row not found.' },
  { key: 'pricing.lightingCatalogMissing', match: 'Lighting catalog row not found' },
  {
    key: 'pricing.drawerIncomplete',
    match:
      'Drawer AUTO pricing requires materialId/drawerSystemVariantId/hbox/drawerModelKind/heightMm/widthMm/depthMm.',
  },
  { key: 'pricing.drawerMaterialMissing', match: 'Drawer material not found.' },
  { key: 'pricing.drawerMaterialMissing', match: 'Drawer material not found' },
  { key: 'pricing.drawerEdgeMissing', match: 'Drawer edge not found.' },
  { key: 'pricing.drawerEdgeMissing', match: 'Drawer edge not found' },
  { key: 'pricing.hboxUnsupported', match: 'Drawer H-box not supported.' },
  { key: 'pricing.hboxUnsupported', match: 'Drawer H-box not supported' },
  { key: 'pricing.hboxRequired', match: 'hbox or hBox is required' },
  {
    key: 'pricing.cabinetNeedsPiece',
    match: 'Cabinet PIECES pricing requires at least one piece.',
  },
  { key: 'pricing.pieceNeedsMaterial', match: 'Every cabinet piece requires a materialId.' },
  {
    key: 'pricing.pieceMaterialMissing',
    match: /^Cabinet piece material not found: (?<label>.+)$/,
  },
  { key: 'pricing.pieceEdgeMissing', match: /^Cabinet piece edge not found: (?<label>.+)$/ },
  { key: 'pricing.anyPieceMaterialMissing', match: 'Piece material not found' },
  { key: 'pricing.anyPieceEdgeMissing', match: 'Piece edge not found' },
  {
    key: 'pricing.cabinetIncomplete',
    match: 'Cabinet AUTO pricing requires materialId and joinTypeKind.',
  },
  { key: 'pricing.carcassMaterialMissing', match: 'Cabinet carcass material not found.' },
  { key: 'pricing.carcassMaterialMissing', match: 'Carcass material not found' },
  { key: 'pricing.carcassEdgeMissing', match: 'Cabinet carcass edge not found.' },
  { key: 'pricing.carcassEdgeMissing', match: 'Carcass edge not found' },
  { key: 'pricing.frontEdgeMissing', match: 'Cabinet front edge not found.' },
  { key: 'pricing.frontEdgeMissing', match: 'Front edge not found' },
  { key: 'pricing.backEdgeMissing', match: 'Cabinet back edge not found.' },
  { key: 'pricing.backEdgeMissing', match: 'Back edge not found' },
  { key: 'pricing.hardwareEmpty', match: 'hardwareItems must not be empty' },
  { key: 'pricing.hardwareMissing', match: /^Hardware \S+ not found$/ },
  { key: 'pricing.financialsPermission', match: 'Missing permission to read project financials.' },
  { key: 'pricing.replacementMissing', match: 'Replacement material not found or deleted.' },
  {
    key: 'pricing.replacementsMissing',
    match: /^Replacement material not found or deleted: (?<materials>.+)\.$/,
  },
  { key: 'pricing.noEdge', match: 'No edge to resolve.' },
  { key: 'planning.planNotFound', match: 'Project plan not found.' },
  { key: 'planning.phaseNotFound', match: 'Project phase not found.' },
  { key: 'planning.completeNotRunning', match: 'Only an in-progress phase can be completed.' },
  { key: 'planning.selfDependency', match: 'Phase cannot depend on itself.' },
  { key: 'planning.graphChanged', match: 'Dependency graph changed while saving; please retry.' },
  { key: 'planning.cycle', match: 'Dependency would create a cycle.' },
  { key: 'planning.capacityFull', match: /^(?<phase>.+) capacity is full\.$/ },
  { key: 'planning.notReady', match: 'Phase is not ready to start.' },
  { key: 'planning.dependenciesOpen', match: 'Phase dependencies are not completed.' },
  { key: 'planning.queued', match: 'Phase is waiting in queue.' },
  { key: 'scheduler.projectNotFound', match: 'Scheduler project not found.' },
  { key: 'scheduler.stageNotFound', match: 'Scheduler stage not found.' },
  {
    key: 'scheduler.projectFinished',
    match: 'This scheduler project is finished and can no longer be changed.',
  },
  {
    key: 'scheduler.reopenOnlyWarranty',
    match: 'Only a finished production project under warranty can be reopened.',
  },
  {
    key: 'scheduler.teamChoiceChanged',
    match: 'The board changed since the teams were shown — review the choice again.',
  },
  {
    key: 'scheduler.teamReserved',
    match: /^Team (?<team>.+) is reserved for another operation until it is done\.$/,
  },
  { key: 'scheduler.teamNotPlanned', match: 'That team is not planned on this operation.' },
  { key: 'scheduler.leaveNotFound', match: 'Leave not found.' },
  { key: 'scheduler.workerNotFound', match: 'Worker not found.' },
  { key: 'scheduler.teamNotFound', match: 'Scheduler team not found.' },
  { key: 'scheduler.alreadyEnrolled', match: 'Project is already in the scheduler.' },
  { key: 'scheduler.needsOperation', match: 'A project must undergo at least one operation.' },
  {
    key: 'scheduler.noSuchTeam',
    match: /^No such team for operation (?<code>.+)\.$/,
    stages: ['code'],
  },
  {
    key: 'scheduler.teamInactive',
    match: /^Team (?<team>.+) is inactive and cannot be assigned to (?<code>.+)\.$/,
    stages: ['code'],
  },
  {
    key: 'scheduler.teamWrongStage',
    match: /^Team (?<team>.+) does not perform (?<code>.+)\.$/,
    stages: ['code'],
  },
  {
    key: 'scheduler.noRate',
    match: 'The average hourly rate is 0; set the operation rates first.',
  },
  {
    key: 'scheduler.valueTooLow',
    match: 'The value does not cover materials, profit, commission and additional costs.',
  },
  { key: 'scheduler.stageTooLong', match: /^An operation would exceed (?<hours>\d+) h\.$/ },
  { key: 'scheduler.noHours', match: 'No operation has any hours to schedule.' },
  {
    key: 'scheduler.lagLocked',
    match:
      /^(?<code>[A-Z_]+) has already started, so when it may start can no longer be changed\.$/,
    stages: ['code'],
  },
  {
    key: 'scheduler.excludeWorked',
    match: /^(?<code>[A-Z_]+) already has recorded work; it cannot be removed from the project\.$/,
    stages: ['code'],
  },
  { key: 'scheduler.entryNotOnStage', match: 'That person is not on this operation.' },
  { key: 'scheduler.entryFutureDay', match: 'Hours cannot be entered on a day still to come.' },
  {
    key: 'scheduler.entryNoRoom',
    match: /^There is no room for those hours on (?<date>\S+) for (?<name>.+)\.$/,
  },
  {
    key: 'scheduler.reopenNotFinished',
    match: /^(?<code>[A-Z_]+) is not finished, so it cannot be reopened\.$/,
    stages: ['code'],
  },
  { key: 'scheduler.shiftDone', match: 'Cannot change the second shift of a completed stage.' },
  { key: 'scheduler.interventionStartFixed', match: "An intervention's start cannot be moved." },
  {
    key: 'scheduler.interventionAddOps',
    match:
      'Operations cannot be added to an intervention; cancel it and reopen with the operations you need.',
  },
  { key: 'scheduler.interventionOnly', match: 'Only a warranty intervention supports this.' },
  {
    key: 'scheduler.afterWarranty',
    match: 'An intervention cannot be scheduled to start after the warranty ends.',
  },
  { key: 'scheduler.estimateDone', match: 'Cannot change the estimate of a completed stage.' },
  {
    key: 'scheduler.interventionEstimate',
    match: 'Every operation of an intervention needs an estimate above 0 h.',
  },
  {
    key: 'scheduler.hoursSkipped',
    match: 'An operation the project does not perform has no hours to edit.',
  },
  { key: 'scheduler.cancelNotRemove', match: 'An intervention is cancelled, not removed.' },
  { key: 'scheduler.warrantyNoRemove', match: 'A project under warranty cannot be removed.' },
  { key: 'scheduler.cancelNotReset', match: 'An intervention is cancelled, not reset.' },
  {
    key: 'scheduler.interventionOpen',
    match: 'The project already has an intervention in progress.',
  },
  {
    key: 'scheduler.warrantyEnded',
    match: 'The warranty has ended; the project cannot be reopened.',
  },
  {
    key: 'scheduler.selectOperations',
    match: 'Select at least one operation, each with an estimate above 0 h.',
  },
  {
    key: 'scheduler.selectOperations',
    match: 'Select at least one operation, each with an estimate of at least one minute.',
  },
  { key: 'scheduler.completeNotRunning', match: 'Only a stage in progress can be completed.' },
  { key: 'scheduler.holdNotRunning', match: 'Only a stage in progress can be put on waiting.' },
  {
    key: 'scheduler.moveNotOnBoard',
    match: 'Only a project on the board can be moved to the scheduling list.',
  },
  { key: 'scheduler.notOnHold', match: 'Stage is not on hold.' },
  {
    key: 'scheduler.startNowNotWaiting',
    match: 'Only a waiting stage of an active project can be started now.',
  },
  { key: 'scheduler.noTeamAtStage', match: 'No team exists at this stage yet — add one first.' },
  {
    key: 'scheduler.allCrewsBusy',
    match: 'Every crew for this operation is busy on another project right now.',
  },
  { key: 'scheduler.proposalStale', match: 'Proposal is no longer valid.' },
  { key: 'scheduler.alertStale', match: 'Alert is no longer valid.' },
  {
    key: 'scheduler.rescueStale',
    match: 'The board changed and the recommendation can no longer be applied as sent.',
  },
  { key: 'scheduler.pickCollaborator', match: 'Pick or create a collaborator.' },
  {
    key: 'scheduler.leaveEnded',
    match: 'This leave has already ended; only current or upcoming leave can be added.',
  },
  { key: 'scheduler.leaveOverlap', match: 'This overlaps another leave of the same worker.' },
  {
    key: 'scheduler.engageCollaboratorOnly',
    match: 'Only a collaborator team can be engaged on a project.',
  },
  { key: 'scheduler.engagementNotFound', match: 'Engagement not found.' },
  { key: 'scheduler.teamNameTaken', match: 'A team with this name already exists.' },
  { key: 'scheduler.entryExists', match: 'That entry already exists.' },
  { key: 'scheduler.changedWhileSaving', match: 'Scheduler changed while saving; please retry.' },
  {
    key: 'scheduler.reinforceNotRunning',
    match: 'Only a stage in progress can take another team.',
  },
  { key: 'scheduler.teamPaused', match: 'Team is paused; resume it before putting it on a stage.' },
  { key: 'scheduler.teamCannot', match: 'Team cannot perform this operation.' },
  { key: 'scheduler.teamAlreadyOn', match: 'Team is already on this stage.' },
  { key: 'scheduler.teamElsewhere', match: 'Team is already working on another project.' },
  { key: 'scheduler.releaseNotRunning', match: 'Only a stage in progress can release a team.' },
  { key: 'scheduler.teamNotOn', match: 'Team is not on this stage.' },
  { key: 'scheduler.teamBusy', match: 'Team is working on a project; complete the stage first.' },
  {
    key: 'scheduler.teamHasWorkers',
    match: 'Team still has workers; delete them or move them to another team first.',
  },
  {
    key: 'scheduler.calendarNoDays',
    match: 'The selected working-days calendar has no working days.',
  },
  { key: 'attendance.sessionNotFound', match: 'Work session not found.' },
  {
    key: 'attendance.clockedInMove',
    match: 'Worker is clocked in; stop the session before reassigning teams.',
  },
  {
    key: 'attendance.clockedInDeactivate',
    match: 'Worker is clocked in; stop the session before deactivating.',
  },
  {
    key: 'attendance.clockedInDelete',
    match: 'Worker is clocked in; stop the session before deleting.',
  },
  {
    key: 'attendance.openSessionEdit',
    match: 'Worker is clocked in on this session; stop it before editing.',
  },
  {
    key: 'attendance.openSessionDelete',
    match: 'Worker is clocked in on this session; stop it before deleting.',
  },
  { key: 'attendance.invalidTime', match: 'Invalid start or end time.' },
  { key: 'attendance.endBeforeStart', match: 'The end time must be after the start time.' },
  {
    key: 'attendance.sessionTooLong',
    match: /^A single session cannot be longer than (?<hours>\d+) hours\.$/,
  },
  { key: 'attendance.sessionOverlap', match: 'This overlaps another session for the same worker.' },
  // An entry for several people names whose day clashes (exact sentences win).
  {
    key: 'attendance.sessionOverlapFor',
    match: /^This overlaps another session for (?<name>.+)\.$/,
  },
  {
    key: 'attendance.periodBackwards',
    match: 'The period must start on or before its end.',
  },
  {
    key: 'attendance.periodTooLong',
    match: /^A period covers at most (?<days>\d+) days\.$/,
  },
  {
    key: 'attendance.hasHistory',
    match:
      'Worker has recorded hours. Confirm the purge to delete them together with that history, or deactivate them instead.',
  },
  { key: 'attendance.workerInactive', match: 'Worker is inactive.' },
  { key: 'attendance.alreadyClockedIn', match: 'Worker is already clocked in.' },
  { key: 'attendance.notClockedIn', match: 'Worker is not clocked in.' },
  {
    key: 'attendance.projectMismatch',
    match:
      'The crew was not on this project in the flow during these hours, and is not on it now. Confirm to save the entry anyway.',
  },
  { key: 'general.nothingToSave', match: 'Update body must contain at least one field.' },
  { key: 'general.nothingToSave', match: 'At least one field is required.' },
  { key: 'general.nothingToSave', match: 'At least one field must be provided.' },
  { key: 'general.recordMissing', match: 'No record was found for the given query.' },
  { key: 'validation.dateBothWays', match: 'A date cannot be both working and non-working.' },
  { key: 'validation.rescueAlone', match: 'A recommendation is previewed on its own.' },
  { key: 'validation.rateOrShare', match: 'At least one rate or share is required.' },
  {
    key: 'validation.whatIfEmpty',
    match: 'At least one stage or a simulated project is required.',
  },
  { key: 'validation.dayBounds', match: 'Both start and end must be set, or both null (day off).' },
  { key: 'validation.choosePhase', match: 'Choose at least one phase.' },
  { key: 'validation.weekdayTwice', match: 'Duplicate weekday in schedule.' },
  { key: 'validation.endAfterStart', match: 'End must be after start.' },
  {
    key: 'validation.workedOverEstimate',
    match: 'Hours already worked cannot exceed the estimate for that operation.',
  },
  { key: 'validation.invalidDate', match: 'Invalid date.' },
  { key: 'validation.invalidDate', match: 'Invalid datetime' },
  { key: 'validation.invalidDate', match: 'Invalid date' },
  { key: 'validation.positiveInteger', match: 'Must be a positive integer' },
  { key: 'validation.positive', match: 'Must be greater than 0' },
  { key: 'validation.positive', match: 'Value must be positive.' },
  { key: 'validation.nonNegative', match: 'Must be ≥ 0' },
  { key: 'validation.newPasswordSame', match: 'New password must differ from the current one.' },
  { key: 'validation.advanceAmount', match: 'Provide hours or days to advance.' },
  { key: 'validation.leaveEndBeforeStart', match: 'The leave must end on or after its start.' },
  { key: 'validation.replacementSame', match: 'The replacement must be a different material.' },
  {
    key: 'validation.lagFloor',
    match:
      /^(?<code>[A-Z_]+) may start no sooner than (?<hours>[\d.]+)h after the previous operation started\.$/,
    stages: ['code'],
  },
  { key: 'validation.maxDays', match: /^At most (?<days>\d+) days\.$/ },
  {
    key: 'validation.programTooShort',
    match: /^The program must end at least (?<minutes>\d+) minutes after it starts\.$/,
  },
  {
    key: 'validation.overtimeInsideProgram',
    match: 'Overtime availability must lie outside the program.',
  },
  {
    key: 'validation.overtimeInsideGeneralProgram',
    match: 'The overtime window must lie outside the general program.',
  },
  {
    key: 'validation.overtimeEndBeforeStart',
    match: 'The overtime window must end after it starts.',
  },
  { key: 'validation.required', match: 'Required' },
  { key: 'validation.invalid', match: 'Invalid input' },
  { key: 'validation.invalid', match: 'Invalid' },
  { key: 'validation.invalidId', match: 'Invalid cuid' },
  { key: 'validation.email', match: 'Invalid email' },
  { key: 'validation.integer', match: 'Expected integer, received float' },
  { key: 'validation.wrongType', match: /^Expected (?<expected>.+), received (?<received>.+)$/ },
  {
    key: 'validation.minChars',
    match: /^String must contain at least (?<min>\d+) character\(s\)$/,
  },
  { key: 'validation.maxChars', match: /^String must contain at most (?<max>\d+) character\(s\)$/ },
  {
    key: 'validation.minNumber',
    match: /^Number must be greater than or equal to (?<min>-?[\d.]+)$/,
  },
  { key: 'validation.maxNumber', match: /^Number must be less than or equal to (?<max>-?[\d.]+)$/ },
  { key: 'validation.gtNumber', match: /^Number must be greater than (?<min>-?[\d.]+)$/ },
  {
    key: 'validation.invalidOption',
    match: /^Invalid enum value\. Expected (?<options>.+), received '(?<received>.*)'$/,
  },
  { key: 'validation.unknownFields', match: /^Unrecognized key\(s\) in object: (?<keys>.+)$/ },
  { key: 'api.routeMissing', match: /^Cannot (?<method>GET|POST|PUT|PATCH|DELETE) (?<path>\S+)$/ },
];

const exact = new Map<string, ServerMessageRule>();
const patterns: ServerMessageRule[] = [];
for (const rule of SERVER_MESSAGE_RULES) {
  if (typeof rule.match === 'string') {
    if (!exact.has(rule.match)) exact.set(rule.match, rule);
  } else {
    patterns.push(rule);
  }
}

/** A server sentence the UI can explain: its key, the values it carries, and which are operations. */
export interface KnownServerMessage {
  key: string;
  values: Record<string, string>;
  stages: readonly string[];
}

/** The explanation of a server sentence, or null when the catalog does not know it. */
export function knownServerMessage(message: string): KnownServerMessage | null {
  const text = message.trim();
  const rule = exact.get(text);
  if (rule) return { key: `errors.server.${rule.key}`, values: {}, stages: [] };
  for (const pattern of patterns) {
    const found = (pattern.match as RegExp).exec(text);
    if (found) {
      return {
        key: `errors.server.${pattern.key}`,
        values: { ...found.groups },
        stages: pattern.stages ?? [],
      };
    }
  }
  return null;
}
